import { useEffect } from 'react';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { Notification } from '../types';

export function useNotificationsStream({
  onNotifications,
  accountIds,
  enabled,
}: {
  onNotifications: (notifications: Notification[]) => void;
  accountIds: string[];
  enabled: boolean;
}) {
  const client = useSupabase();

  useEffect(() => {
    // Nothing to watch. An empty `in.()` filter is itself invalid — Realtime
    // parses `account_id=in.()` and casts the empty list as one bad uuid
    // (`invalid input syntax for type uuid`) — so never open a channel without
    // at least one account id.
    if (!enabled || accountIds.length === 0) {
      return;
    }

    // Tracks the channel opened by the most recent `openChannel()` run so a
    // teardown (effect cleanup, or a re-subscribe on auth change) removes
    // exactly the live channel and never a stale one.
    let channel: ReturnType<typeof client.channel> | null = null;
    // Cancellation guard for the async setup below (mirrors use-presence): if
    // this effect is torn down before getSession() resolves, we must NOT bind
    // a channel that is about to be orphaned.
    let cancelled = false;
    // Supersedes an in-flight openChannel() when a newer one starts (e.g. the
    // initial run races an onAuthStateChange re-subscribe). Only the latest
    // run is allowed to install its channel.
    let currentRun = 0;

    const teardown = () => {
      if (channel) {
        void channel.unsubscribe();
        void client.removeChannel(channel);
        channel = null;
      }
    };

    const openChannel = async () => {
      const runId = ++currentRun;

      // The Realtime socket may still carry the anon JWT right after mount
      // (before the authed token propagates). `notifications` has no grant for
      // `anon`, so Realtime's filter-validation trigger can't see the
      // `account_id` column and raises `invalid column for filter account_id`
      // (logged as role supabase_admin). Gate on a live session and push its
      // access token onto the Realtime socket BEFORE subscribing, so the
      // filter validates against an authenticated role.
      const {
        data: { session },
      } = await client.auth.getSession();

      if (cancelled || runId !== currentRun) return;

      // No live session → do not subscribe as anon.
      if (!session) return;

      await client.realtime.setAuth(session.access_token);

      if (cancelled || runId !== currentRun) return;

      // Per-run channel name so any re-subscription (Strict Mode, changed
      // callback ref, changed accountIds, token refresh) gets a fresh channel
      // instance. Supabase Realtime SDK rejects `.on()` after `.subscribe()`
      // with `cannot add postgres_changes callbacks for realtime:<name>
      // after subscribe()`, and `client.channel(name)` returns the same
      // singleton for the same name. `crypto.randomUUID()` guarantees
      // uniqueness on every run. See BAL-149.
      const channelName = `notifications-channel-${crypto.randomUUID()}`;
      const nextChannel = client.channel(channelName);

      nextChannel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            // No space after the comma: Realtime's `in.()` parser treats
            // `in.(a, b)` (with spaces) as a single malformed uuid, raising
            // `invalid input syntax for type uuid`. `join(',')` keeps the
            // list valid.
            filter: `account_id=in.(${accountIds.join(',')})`,
            table: 'notifications',
          },
          (payload) => {
            onNotifications([payload.new as Notification]);
          },
        )
        .subscribe();

      channel = nextChannel;
    };

    void openChannel();

    // A token refresh (or a sign-in completing after mount) swaps the socket's
    // JWT. Tear down the channel opened against the stale token and re-mint one
    // against the fresh token so the filter keeps validating.
    const { data: authListener } = client.auth.onAuthStateChange(() => {
      teardown();
      void openChannel();
    });

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
      teardown();
    };
  }, [client, onNotifications, accountIds, enabled]);
}
